import React, { useEffect, useState } from 'react';
import { dialogConfigs } from '../Utils/PMHelper';
import { TChannel } from '../Types/channel.types';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';

interface CreateChannelDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    isLoading?: boolean;
    editData?: TChannel | null;
    mode?: 'create' | 'edit';
    id?: string;
}

/**
 * Create / Edit Channel on the design-system `Dialog` (§ 120) — the same swap §§ 118
 * and 119 made for the party and domain dialogs. `Components/CustomDialog` is now down
 * to ONE consumer, `AddEnvironmentDialog`.
 *
 * Fixes here, all inherited from `CustomDialog`: no testids on Save/Cancel/Close (its
 * hooks are `id ? … : undefined` and this caller never passed an `id`), a `<label>`
 * with no `htmlFor` beside an input with no `id`, and a plain `<div>` overlay with no
 * `role="dialog"`, no focus trap and no Escape.
 *
 * Behaviour unchanged: one required field, `onSave` receives `{ channelName }` for the
 * parent to map, and the "Creating..." busy label stays wrong in edit mode for the
 * reason given in § 118. There is no required-field validation, here or before.
 */
export const CreateChannelDialog = ({
    isOpen,
    onClose,
    onSave,
    isLoading = false,
    editData = null,
    mode = 'create',
    id,
}: CreateChannelDialogProps) => {
    const [channelName, setChannelName] = useState('');

    const baseId = id || `channel-${mode}-dialog`;

    /* Seed on open — see § 119 on why there is no reset in a close branch. */
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setChannelName(mode === 'edit' && editData ? editData.name : '');
    }, [isOpen]);

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            title={mode === 'edit' ? 'Edit Channel' : dialogConfigs.createChannel.title}
            size="md"
            data-testid={baseId}
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <Button
                        variant="secondary"
                        id={`${baseId}-cancel-btn`}
                        data-testid={`${baseId}-cancel-btn`}
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        id={`${baseId}-save-btn`}
                        data-testid={`${baseId}-save-btn`}
                        onClick={() => onSave({ channelName })}
                        /* § 209 — loading implies the disabled gate this carried,
                           plus the spinner, aria-busy and the wait cursor. */
                        loading={isLoading}
                        className="tw-min-w-[92px]"
                    >
                        {isLoading ? 'Creating...' : (mode === 'edit' ? 'Update' : 'Create')}
                    </Button>
                </div>
            }
        >
            <TextField
                label="Channel Name"
                required
                placeholder="Enter"
                value={channelName}
                onChange={setChannelName}
                disabled={isLoading}
                id={`${baseId}-name`}
                data-testid={`${baseId}-name`}
            />
        </Dialog>
    );
};
